import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { SortableContext, verticalListSortingStrategy, rectSortingStrategy } from '@dnd-kit/sortable';
import {
  RiDraggable, RiDeleteBinLine, RiFileCopyLine,
  RiArrowDownSLine, RiArrowUpSLine, RiAddLine, RiTableLine,
  RiCheckLine, RiCloseLine, RiAlertLine, RiLayoutRowLine,
  RiGridLine, RiEyeLine, RiEyeOffLine, RiLock2Line,
} from 'react-icons/ri';
import { FIELD_TYPES, SECTION_COLORS } from './constants';
import { fieldTypeLabel } from './utils';
import MultiLangInput from '../../components/MultiLangInput/MultiLangInput';
import FieldRow from './FieldRow';
import FieldDropZone from './FieldDropZone';
import TableColumnRow from './TableColumnRow';
import TablePreview from './TablePreview';

export default function SectionCard({
  section,
  onUpdate, onRemove, onDuplicate,
  onAddField, onAddColumn,
  onUpdateField, onRemoveField,
  onUpdateColumn, onRemoveColumn,
  errors,
  isDragOverlay,
}) {
  const { t, i18n } = useTranslation();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [addFieldOpen, setAddFieldOpen] = useState(false);
  const addFieldRef = useRef(null);
  const colorPickerRef = useRef(null);

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: section.id,
    data: { type: 'section' },
    disabled: !!isDragOverlay,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    '--section-color': section.color,
  };

  // Close add-field popover on outside click
  useEffect(() => {
    if (!addFieldOpen) return;
    const handler = (e) => {
      if (addFieldRef.current && !addFieldRef.current.contains(e.target)) {
        setAddFieldOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [addFieldOpen]);

  // Close color picker on outside click
  useEffect(() => {
    if (!showColorPicker) return;
    const handler = (e) => {
      if (colorPickerRef.current && !colorPickerRef.current.contains(e.target)) {
        setShowColorPicker(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showColorPicker]);

  const fieldIds = section.fields ? section.fields.map((f) => f.id) : [];
  const colIds = section.tableColumns ? section.tableColumns.map((c) => c.id) : [];
  const fieldCount = section.type === 'fields' ? (section.fields?.length || 0) : (section.tableColumns?.length || 0);

  const sectionStyle = isDragOverlay
    ? { '--section-color': section.color }
    : style;

  return (
    <div
      ref={setNodeRef}
      style={sectionStyle}
      className={`fm-section ${isDragging ? 'fm-section--dragging' : ''} ${isDragOverlay ? 'fm-section--overlay' : ''} ${section.isActive === false ? 'fm-section--passive' : ''}`}
    >
      {/* ── Section Header ── */}
      <div className="fm-section__header">
        {/* Drag handle */}
        <span
          className="fm-drag-handle fm-drag-handle--section"
          {...(isDragOverlay ? {} : { ...attributes, ...listeners })}
          title={t('fieldsManager.sectionCard.dragToReorder')}
        >
          <RiDraggable />
        </span>

        {/* Color dot */}
        <div className="fm-section__color-wrap" ref={colorPickerRef}>
          <span
            className="fm-section__color-dot"
            style={{ background: section.color }}
            onClick={() => setShowColorPicker((v) => !v)}
            title={t('fieldsManager.sectionCard.changeColor')}
          />
          {showColorPicker && (
            <div className="fm-color-picker">
              {SECTION_COLORS.map((c) => (
                <button
                  key={c}
                  className={`fm-color-swatch ${section.color === c ? 'fm-color-swatch--active' : ''}`}
                  style={{ background: c }}
                  onClick={() => { onUpdate(section.id, { color: c }); setShowColorPicker(false); }}
                  title={c}
                />
              ))}
            </div>
          )}
        </div>

        {/* Title input */}
        <div className="fm-section__title-wrap">
          <MultiLangInput
            value={section.title}
            onChange={(v) => onUpdate(section.id, { title: v })}
            primaryLang={i18n.language}
            placeholder={t('fieldsManager.sectionCard.namePlaceholder')}
            className="fm-ml fm-ml--section"
            inputClassName={`fm-section-title-input ${errors[`section-${section.id}-title`] ? 'fm-input--error' : ''}`}
          />
          {errors[`section-${section.id}-title`] && (
            <span className="fm-error-text">{errors[`section-${section.id}-title`]}</span>
          )}
        </div>

        {/* Type badge */}
        <span className={`fm-section-type-badge fm-section-type-badge--${section.type}`}>
          {section.type === 'table'
            ? <><RiTableLine /> {t('fieldsManager.sectionCard.table')}</>
            : <><RiLayoutRowLine /> {t('fieldsManager.sectionCard.fields')}</>
          }
        </span>

        {/* Field count */}
        {fieldCount > 0 && (
          <span className="fm-field-count-badge">
            {section.type === 'table'
              ? t('fieldsManager.sectionCard.colsCount', { count: fieldCount })
              : t('fieldsManager.sectionCard.fieldsCount', { count: fieldCount })}
          </span>
        )}

        {/* Column error */}
        {errors[`section-${section.id}-columns`] && (
          <span className="fm-error-text fm-error-text--inline">
            <RiAlertLine /> {errors[`section-${section.id}-columns`]}
          </span>
        )}

        {/* Passive marker */}
        {section.isActive === false && (
          <span className="fm-section-passive-badge">{t('common.inactive')}</span>
        )}

        {/* Actions */}
        <div className="fm-section__actions">
          <button
            className="fm-icon-btn fm-icon-btn--ghost"
            onClick={() => onDuplicate(section.id)}
            title={t('fieldsManager.sectionCard.duplicate')}
          >
            <RiFileCopyLine />
          </button>

          {section.isStandard ? (
            // Standard sections: no delete — only active/passive.
            <button
              className={`fm-icon-btn ${section.isActive === false ? 'fm-icon-btn--danger' : 'fm-icon-btn--ghost'}`}
              onClick={() => onUpdate(section.id, { isActive: section.isActive === false })}
              title={section.isActive === false
                ? t('fieldsManager.standardSection.activate', { defaultValue: 'Activate this section' })
                : t('fieldsManager.standardSection.deactivate', { defaultValue: 'Set this section passive' })}
            >
              {section.isActive === false ? <RiEyeOffLine /> : <RiEyeLine />}
            </button>
          ) : confirmDelete ? (
            <div className="fm-confirm-delete">
              <span>{t('fieldsManager.sectionCard.deleteSection')}</span>
              <button
                className="fm-btn fm-btn--danger fm-btn--xs"
                onClick={() => { onRemove(section.id); setConfirmDelete(false); }}
              >
                <RiCheckLine /> {t('common.yes')}
              </button>
              <button
                className="fm-btn fm-btn--ghost fm-btn--xs"
                onClick={() => setConfirmDelete(false)}
              >
                <RiCloseLine />
              </button>
            </div>
          ) : (
            <button
              className="fm-icon-btn fm-icon-btn--danger"
              onClick={() => setConfirmDelete(true)}
              title={t('fieldsManager.sectionCard.delete')}
            >
              <RiDeleteBinLine />
            </button>
          )}

          <button
            className="fm-icon-btn fm-icon-btn--ghost"
            onClick={() => onUpdate(section.id, { collapsed: !section.collapsed })}
            title={section.collapsed ? t('fieldsManager.sectionCard.expand') : t('fieldsManager.sectionCard.collapse')}
          >
            {section.collapsed ? <RiArrowDownSLine /> : <RiArrowUpSLine />}
          </button>
        </div>
      </div>

      {/* Standard-section note */}
      {section.isStandard && (
        <div className="fm-section__std-note">
          <RiLock2Line />
          <span>{t('fieldsManager.standardSection.note', { defaultValue: 'This standard section cannot be deleted. It can be deactivated if needed.' })}</span>
        </div>
      )}

      {/* ── Section Body ── */}
      {!section.collapsed && (
        <div className="fm-section__body">
          {/* ── Fields Section ── */}
          {section.type === 'fields' && (
            <>
              {section.fields.length > 0 && (
                <div className="fm-field-grid">
                  <SortableContext items={fieldIds} strategy={rectSortingStrategy}>
                    {section.fields.map((field) => (
                      <FieldRow
                        key={field.id}
                        field={field}
                        sectionId={section.id}
                        sectionFields={section.fields}
                        onUpdate={onUpdateField}
                        onRemove={onRemoveField}
                        errors={errors}
                      />
                    ))}
                  </SortableContext>
                </div>
              )}

              {/* Drop target: always shown while the section is empty, and
                  revealed under the last field whenever a field is dragged. */}
              {!isDragOverlay && (
                <FieldDropZone
                  sectionId={section.id}
                  alwaysVisible={section.fields.length === 0}
                />
              )}

              {/* Add field footer */}
              <div className="fm-section__footer" ref={addFieldRef}>
                <button
                  className="fm-btn fm-btn--ghost fm-btn--sm"
                  onClick={() => setAddFieldOpen((v) => !v)}
                  style={{ borderStyle: 'dashed' }}
                >
                  <RiAddLine /> {t('fieldsManager.sectionCard.addField')}
                </button>
                {addFieldOpen && (
                  <div className="fm-add-field-popover">
                    {FIELD_TYPES.map((ft) => {
                      const Icon = ft.icon;
                      return (
                        <button
                          key={ft.type}
                          className="fm-add-field-option"
                          style={{ '--ft-color': ft.color, '--ft-bg': ft.bg }}
                          onClick={() => { onAddField(section.id, ft.type); setAddFieldOpen(false); }}
                        >
                          <span className="fm-add-field-option__icon"><Icon /></span>
                          {fieldTypeLabel(t, ft)}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          )}

          {/* ── Table Section ── */}
          {section.type === 'table' && (
            <>
              <div className="fm-table-columns">
                <div className="fm-table-columns__header">
                  <span className="fm-table-columns__header-label">
                    <RiGridLine /> {t('fieldsManager.sectionCard.tableColumns')}
                  </span>
                </div>
                <SortableContext items={colIds} strategy={verticalListSortingStrategy}>
                  {(section.tableColumns || []).map((col) => (
                    <TableColumnRow
                      key={col.id}
                      col={col}
                      sectionId={section.id}
                      onUpdate={onUpdateColumn}
                      onRemove={onRemoveColumn}
                      errors={errors}
                    />
                  ))}
                </SortableContext>
                {(!section.tableColumns || section.tableColumns.length === 0) && (
                  <div className="fm-section__empty" style={{ margin: 12, borderRadius: 10 }}>
                    <span className="fm-section__empty-icon">📊</span>
                    <p className="fm-section__empty-title">{t('fieldsManager.sectionCard.noColumnsYet')}</p>
                    <p className="fm-section__empty-hint">{t('fieldsManager.sectionCard.addColumnHint')}</p>
                  </div>
                )}
                <div className="fm-section__footer" style={{ padding: '10px 14px' }}>
                  <button
                    className="fm-btn fm-btn--ghost fm-btn--sm"
                    onClick={() => onAddColumn(section.id)}
                    style={{ borderStyle: 'dashed' }}
                  >
                    <RiAddLine /> {t('fieldsManager.sectionCard.addColumn')}
                  </button>
                </div>
              </div>
              <TablePreview columns={section.tableColumns || []} />
            </>
          )}
        </div>
      )}
    </div>
  );
}
